// Owned by task 2.7 of the dashboard-redesign spec (Requirement 11).
//
// Reads the recent-activity feed by issuing THREE parallel GETs to the
// financial-records, statements, and reconciliation source specs, merging
// their results client-side, sorting descending by timestamp (with a
// lexicographic tie-break on `(kind, id)`), and returning the top `limit`
// events. Every source is read-only; nothing on this hook mutates.
//
// Fetch contract (see design.md §Low-Level Design + Req 11):
//   - Starts in `{ status: "loading" }`.
//   - On mount and every `scope`/`limit` change: ONE `AbortController`
//     shared across THREE parallel `api.get` calls issued via
//     `Promise.allSettled`. All three fetches therefore abort together on
//     unmount.
//   - All three rejected → error state. The `errorId` is taken from
//     whichever rejection carries an `X-Error-Id` header; if none do we
//     generate a UUID v4 locally.
//   - 1 or 2 rejected but at least 1 succeeded → **ready** state with the
//     merged events from the successful sources PLUS a `partialFailures`
//     array listing each failed source and its `errorId` so the widget can
//     render the inline notice required by Requirement 11.5.
//   - All three succeeded but the merged event list is empty → `empty`.
//   - Otherwise → `ready` with `partialFailures: []`.
//   - `retry` re-invokes all three fetches by bumping a retry counter.
//   - On unmount: aborts every in-flight fetch.
//
// The `api` client (frontend/src/services/api.ts) already attaches the
// Clerk JWT and the `x-clerk-subdomain` tenant header via interceptors —
// this hook MUST NOT re-implement any of that.
//
// Coordination note: the small helpers below (UUID v4, `X-Error-Id`
// extraction) are duplicated inline on purpose, matching the pattern in
// the other Wave-1 hooks (`useDashboardArrears`, `useCollectionRate`,
// `useReserveFundPosition`, `useCashAtBank`, `useUpcomingCompliance`,
// `useUpcomingMeetings`). A later refactor task will consolidate them
// into a `useWidgetFetch` primitive once every Wave-1 hook has landed.

import { useCallback, useEffect, useState } from "react";
import type { AxiosError } from "axios";
import api from "../../../services/api";
import type {
  ActivityEvent,
  BuildingScope,
  FinancialRecord,
  ReconciliationMatch,
  Statement,
  WidgetResult,
} from "../types";

/** Default number of events surfaced (Req 11.1). */
const DEFAULT_LIMIT = 25;

/** User-safe message shown in the scoped error card. Never leaks server text. */
const USER_SAFE_ERROR_MESSAGE = "Could not load recent activity";

/** The three source specs powering the merged feed (Req 11.1). */
export type RecentActivitySource =
  | "financial-records"
  | "statements"
  | "reconciliation";

/**
 * Extended ready-state shape carrying `partialFailures`. When 1 or 2 of the
 * three source fetches rejected but at least one succeeded, the hook stays
 * in `ready` and records the failed sources here so the widget can render
 * the inline notice required by Requirement 11.5 without demoting the
 * whole feed to the error state.
 */
export interface RecentActivityReady {
  status: "ready";
  data: ActivityEvent[];
  /** Non-empty when 1-2 of the 3 sources failed but others succeeded. */
  partialFailures: Array<{ source: RecentActivitySource; errorId: string }>;
}

/** The full return type is the base union plus the extended ready shape. */
export type RecentActivityResult =
  | WidgetResult<ActivityEvent[]>
  | RecentActivityReady;

/**
 * Generate a UUID v4. Prefers `globalThis.crypto.randomUUID` when available;
 * falls back to a small `Math.random`-based hex formatter for environments
 * that don't expose the WebCrypto API (older test runners, JSDOM shims,
 * etc.). Never used to authenticate anything — only to correlate a UI
 * error card to a log entry when the server didn't return an `X-Error-Id`
 * header.
 */
function generateLocalErrorId(): string {
  const cryptoRef = (globalThis as { crypto?: Crypto }).crypto;
  if (cryptoRef && typeof cryptoRef.randomUUID === "function") {
    return cryptoRef.randomUUID();
  }
  // RFC 4122 v4 shape: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx where y ∈ {8,9,a,b}.
  const rand = (): number => Math.floor(Math.random() * 16);
  const hex = (n: number): string => n.toString(16);
  let out = "";
  for (let i = 0; i < 36; i += 1) {
    if (i === 8 || i === 13 || i === 18 || i === 23) {
      out += "-";
    } else if (i === 14) {
      out += "4";
    } else if (i === 19) {
      out += hex((rand() & 0x3) | 0x8);
    } else {
      out += hex(rand());
    }
  }
  return out;
}

/**
 * Extract an `X-Error-Id` header from an axios error's response. Case-
 * insensitive per RFC 7230; different transports normalise headers
 * differently (Node http lowercases everything, JSDOM leaves them as-is).
 */
function extractErrorIdFromAxios(err: unknown): string | undefined {
  if (!err || typeof err !== "object") return undefined;
  const response = (err as AxiosError).response;
  if (!response || !response.headers) return undefined;
  const headers = response.headers as
    | Record<string, string | undefined>
    | { get?: (key: string) => string | null };
  // Some axios versions expose headers as an AxiosHeaders instance with a
  // `get(key)` method; others expose a plain record. Handle both defensively.
  if (typeof (headers as { get?: unknown }).get === "function") {
    const raw = (headers as { get: (key: string) => string | null }).get(
      "x-error-id",
    );
    if (typeof raw === "string" && raw.length > 0) return raw;
  }
  const record = headers as Record<string, string | undefined>;
  const candidate =
    record["x-error-id"] ??
    record["X-Error-Id"] ??
    record["X-ERROR-ID"];
  return typeof candidate === "string" && candidate.length > 0
    ? candidate
    : undefined;
}

/**
 * Validate that a row from any of the three source endpoints carries the
 * common envelope fields we need (id, ts, buildingId, buildingName). Rows
 * that don't are silently skipped (Req: "skip malformed rows silently").
 */
interface CommonEnvelope {
  id: string;
  ts: string;
  buildingId: string;
  buildingName: string;
  raw: Record<string, unknown>;
}

function readEnvelope(row: unknown): CommonEnvelope | undefined {
  if (!row || typeof row !== "object") return undefined;
  const r = row as Record<string, unknown>;
  if (
    typeof r.id !== "string" ||
    r.id.length === 0 ||
    typeof r.ts !== "string" ||
    r.ts.length === 0 ||
    typeof r.buildingId !== "string" ||
    r.buildingId.length === 0 ||
    typeof r.buildingName !== "string"
  ) {
    return undefined;
  }
  return {
    id: r.id,
    ts: r.ts,
    buildingId: r.buildingId,
    buildingName: r.buildingName,
    raw: r,
  };
}

/**
 * Transform financial-records rows into `bill_created` / `payment_settled`
 * events. Rule (from task 2.7 brief):
 *   - `record.type === "payment"` OR `record.settledAt` OR
 *     `record.amountPaid > 0` → `payment_settled`
 *   - otherwise → `bill_created` (safest default when the shape is ambiguous)
 */
function transformFinancialRecords(rows: unknown): ActivityEvent[] {
  if (!Array.isArray(rows)) return [];
  const events: ActivityEvent[] = [];
  for (const row of rows) {
    const env = readEnvelope(row);
    if (!env) continue;
    const type =
      typeof env.raw.type === "string" ? (env.raw.type as string) : undefined;
    const settledAt = env.raw.settledAt;
    const amountPaid = env.raw.amountPaid;
    const hasSettled =
      (typeof settledAt === "string" && settledAt.length > 0) ||
      (typeof amountPaid === "number" && amountPaid > 0);
    const kind: "bill_created" | "payment_settled" =
      type === "payment" || hasSettled ? "payment_settled" : "bill_created";
    events.push({
      kind,
      ts: env.ts,
      buildingId: env.buildingId,
      buildingName: env.buildingName,
      // We only validated the envelope, not the full FinancialRecord shape.
      // The widget consumes a handful of fields; anything richer that the
      // endpoint provides is passed through opaquely.
      record: env.raw as unknown as FinancialRecord,
    });
  }
  return events;
}

/** Transform statements rows into `statement_issued` events. */
function transformStatements(rows: unknown): ActivityEvent[] {
  if (!Array.isArray(rows)) return [];
  const events: ActivityEvent[] = [];
  for (const row of rows) {
    const env = readEnvelope(row);
    if (!env) continue;
    events.push({
      kind: "statement_issued",
      ts: env.ts,
      buildingId: env.buildingId,
      buildingName: env.buildingName,
      statement: env.raw as unknown as Statement,
    });
  }
  return events;
}

/** Transform reconciliation rows into `reconciliation_matched` events. */
function transformReconciliation(rows: unknown): ActivityEvent[] {
  if (!Array.isArray(rows)) return [];
  const events: ActivityEvent[] = [];
  for (const row of rows) {
    const env = readEnvelope(row);
    if (!env) continue;
    events.push({
      kind: "reconciliation_matched",
      ts: env.ts,
      buildingId: env.buildingId,
      buildingName: env.buildingName,
      match: env.raw as unknown as ReconciliationMatch,
    });
  }
  return events;
}

/**
 * Derive the sort id for an event — the `id` on the underlying record /
 * statement / reconciliation match. Used only for the deterministic
 * `(kind, id)` tie-break when two events share the same `ts` (P6).
 */
function eventSortId(ev: ActivityEvent): string {
  switch (ev.kind) {
    case "bill_created":
    case "payment_settled": {
      const r = ev.record as { id?: unknown };
      return typeof r.id === "string" ? r.id : "";
    }
    case "statement_issued": {
      const s = ev.statement as { id?: unknown };
      return typeof s.id === "string" ? s.id : "";
    }
    case "reconciliation_matched": {
      const m = ev.match as { id?: unknown };
      return typeof m.id === "string" ? m.id : "";
    }
  }
}

/**
 * DESC on `ts`, then ASC lexicographic on `(kind, id)` — a total order.
 * Guarantees the P6 monotone-feed property in requirements.md §11.
 */
function compareActivityEvents(a: ActivityEvent, b: ActivityEvent): number {
  if (a.ts !== b.ts) {
    // DESC on ts — the newer event comes first.
    return a.ts < b.ts ? 1 : -1;
  }
  if (a.kind !== b.kind) {
    return a.kind < b.kind ? -1 : 1;
  }
  const aid = eventSortId(a);
  const bid = eventSortId(b);
  if (aid === bid) return 0;
  return aid < bid ? -1 : 1;
}

function buildUrl(
  base: string,
  scope: BuildingScope,
  limit: number,
): string {
  const buildingId = scope.kind === "all" ? "ALL" : scope.buildingId;
  return `${base}?buildingId=${encodeURIComponent(buildingId)}&limit=${limit}`;
}

/**
 * `useRecentActivity` — Requirement 11.
 *
 * Fetches three sources in parallel and merges them into a single
 * time-sorted feed. See the module header for the full fetch contract.
 */
export function useRecentActivity(
  scope: BuildingScope,
  limit: number = DEFAULT_LIMIT,
): RecentActivityResult {
  const [retryCounter, setRetryCounter] = useState(0);
  const [result, setResult] = useState<RecentActivityResult>({
    status: "loading",
  });

  const retry = useCallback(() => {
    setRetryCounter((n) => n + 1);
  }, []);

  const scopeKind = scope.kind;
  const scopeBuildingId = scope.kind === "single" ? scope.buildingId : null;

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    // Reset to loading on scope/retry/limit change so consumers see the
    // skeleton between fetches instead of stale ready data.
    setResult({ status: "loading" });

    const effectiveScope: BuildingScope =
      scopeKind === "single" && scopeBuildingId
        ? { kind: "single", buildingId: scopeBuildingId }
        : { kind: "all" };

    const frUrl = buildUrl(
      "/financial-records/recent",
      effectiveScope,
      limit,
    );
    const stmtUrl = buildUrl("/statements/recent", effectiveScope, limit);
    const reconUrl = buildUrl("/reconciliation/recent", effectiveScope, limit);

    // ONE AbortController, shared across all three requests. Passing the
    // same signal to each `api.get` means a single `controller.abort()`
    // (on unmount, scope change, or retry) tears every fetch down together.
    const sources: Array<{
      name: RecentActivitySource;
      promise: Promise<unknown>;
    }> = [
      {
        name: "financial-records",
        promise: api.get(frUrl, { signal: controller.signal }),
      },
      {
        name: "statements",
        promise: api.get(stmtUrl, { signal: controller.signal }),
      },
      {
        name: "reconciliation",
        promise: api.get(reconUrl, { signal: controller.signal }),
      },
    ];

    Promise.allSettled(sources.map((s) => s.promise))
      .then((settled) => {
        if (cancelled) return;
        if (controller.signal.aborted) return;

        const events: ActivityEvent[] = [];
        const partialFailures: RecentActivityReady["partialFailures"] = [];
        let successCount = 0;
        let failureCount = 0;

        for (let i = 0; i < settled.length; i += 1) {
          const outcome = settled[i];
          const source = sources[i].name;
          if (outcome.status === "fulfilled") {
            successCount += 1;
            const body = (outcome.value as { data?: unknown } | undefined)
              ?.data;
            if (source === "financial-records") {
              events.push(...transformFinancialRecords(body));
            } else if (source === "statements") {
              events.push(...transformStatements(body));
            } else {
              events.push(...transformReconciliation(body));
            }
          } else {
            failureCount += 1;
            // An abort surfaces here as a rejection whose signal is
            // `.aborted === true`; we ignored those cases above via the
            // top-of-`.then` guard, so at this point every rejection is a
            // real failure worth surfacing.
            const errorId =
              extractErrorIdFromAxios(outcome.reason) ?? generateLocalErrorId();
            partialFailures.push({ source, errorId });
          }
        }

        // All three failed → single scoped error card. Prefer whichever
        // errorId we already extracted from the first failing source; the
        // widget only ever shows one ErrorId in the error state.
        if (failureCount === sources.length) {
          const firstErrorId =
            partialFailures[0]?.errorId ?? generateLocalErrorId();
          setResult({
            status: "error",
            errorId: firstErrorId,
            message: USER_SAFE_ERROR_MESSAGE,
            retry,
          });
          return;
        }

        // Sort DESC by ts with lex `(kind, id)` tie-break, then cap.
        events.sort(compareActivityEvents);
        const capped = events.slice(0, limit);

        // Only mark empty when NOTHING failed and NOTHING came back. If
        // any source failed we stay in `ready` (with `partialFailures`)
        // so Req 11.5's inline notice is preserved.
        if (capped.length === 0 && failureCount === 0 && successCount > 0) {
          setResult({ status: "empty" });
          return;
        }

        setResult({
          status: "ready",
          data: capped,
          partialFailures,
        });
      })
      .catch(() => {
        // `Promise.allSettled` never rejects, so this is defensive only —
        // if some future refactor swaps in `Promise.all`, we want to fail
        // loudly with a scoped error card rather than silently hang.
        if (cancelled) return;
        if (controller.signal.aborted) return;
        setResult({
          status: "error",
          errorId: generateLocalErrorId(),
          message: USER_SAFE_ERROR_MESSAGE,
          retry,
        });
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
    // `scopeKind`, `scopeBuildingId`, `limit`, and `retryCounter` are the
    // effect's inputs; `retry` and `api` are stable refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKind, scopeBuildingId, limit, retryCounter]);

  return result;
}
